import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Bell, Radio, Clock, Bike, Ship, Car, ChevronDown, Sun, Moon, Menu, MessageSquare, X, Calendar } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';
import { ModeToggle } from '@/components/mode-toggle';
import Login from '@/components/auth/Login';

export const Header: React.FC = () => {
  const [locationInfo, setLocationInfo] = useState("");
  const [showClockTooltip, setShowClockTooltip] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hora = now.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo'
      });
      setLocationInfo(`${hora} - Niterói`);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  // Fechar menu quando clicar fora, redimensionar tela ou rolar para baixo
  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMenuOpen(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.mobile-menu') && !target.closest('.menu-button')) {
        setIsMenuOpen(false);
      }
    };

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Fecha o menu se estiver rolando para baixo
      if (currentScrollY > lastScrollY && isMenuOpen) {
        setIsMenuOpen(false);
      }
      
      lastScrollY = currentScrollY;
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMenuOpen]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };



  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between px-4">
        {/* Logo */}
        <div className="flex items-center">
          <Link to="/" className="flex items-center space-x-2">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <motion.span
                className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent"
                animate={{
                  backgroundPosition: ['0%', '100%'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatType: 'reverse',
                }}
              >
                NITEROI CITY
              </motion.span>
              <motion.div
                className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-blue-600 to-blue-400"
                animate={{
                  scaleX: [0, 1, 0],
                }}
                transition={{
                  duration: 7,
                  repeat: Infinity,
                }}
              />
            </motion.div>
          </Link>
        </div>

        {/* Menu Desktop - Visível apenas em telas grandes */}
        <div className="hidden lg:flex items-center space-x-4">
          <Link to="/agendaculturalniteroi" className="flex items-center space-x-2 text-sm font-medium hover:text-primary transition-colors">
            <Calendar className="w-5 h-5" />
            <span>Agenda Cultural de Niterói</span>
          </Link>
          {/* Dropdown Mobilidade */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center space-x-2">
                <Bike className="w-5 h-5" />
                <span>Mobilidade</span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to="/bike-niteroi" className="flex items-center space-x-2">
                  <Bike className="w-4 h-4" />
                  <span>Bike Niterói</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/transito" className="flex items-center space-x-2">
                  <Car className="w-4 h-4" />
                  <span>Trânsito</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/barcas" className="flex items-center space-x-2">
                  <Ship className="w-4 h-4" />
                  <span>Barcas</span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Chat AI */}
          <Button variant="ghost" asChild className="flex items-center space-x-2" title="Abrir chat em nova aba">
            <a href="https://chat.niteroi.city" target="_blank" rel="noopener noreferrer">
              <MessageSquare className="w-5 h-5" />
              <span>Niki City AI</span>
            </a>
          </Button>

          {/* Horário e Data */}
          <div className="flex flex-col items-end">
            <div className="flex items-center space-x-2 text-sm">
              <Clock className="w-4 h-4" />
              <span>{formatTime(new Date())}</span>
            </div>
            <span className="text-xs text-muted-foreground">{formatDate(new Date())}</span>
          </div>

          {/* Rádio */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.open("https://live-radio-clubnation.pantheonsite.io/", "_blank")}
            className="relative group"
          >
            <Radio className="w-5 h-5 animate-pulse" />
            <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
              Rádio
            </span>
          </Button>

          {/* Notificações */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <Bell className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>Nenhuma notificação</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Tema */}
          <ModeToggle />

          {/* Login/Cadastro ou Perfil */}
          <div className="flex items-center space-x-2">
            {/* Remover esta linha:
            {user ? (
              <Button
                variant="destructive"
                onClick={signOut}
              >
                Sair
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')}>
                  Entrar
                </Button>
                <Button onClick={() => navigate('/cadastro')}>
                  Cadastrar
                </Button>
              </>
            )} */}
            <Login />
          </div>
        </div>

        {/* Menu Mobile - Visível apenas em telas pequenas */}
        <div className="lg:hidden flex items-center space-x-2">
          {/* Horário Mobile */}
          <div className="flex items-center space-x-1 text-sm">
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">{formatTime(new Date())}</span>
          </div>

          {/* Botão do Menu */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMenu}
            className="menu-button"
          >
            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Menu Mobile Overlay */}
        <AnimatePresence>
          {isMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                onClick={() => setIsMenuOpen(false)}
              />
              
              {/* Menu Sidebar */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'tween', duration: 0.3 }}
                className="mobile-menu fixed top-16 right-0 w-80 h-[calc(100vh-4rem)] bg-background border-l z-50 lg:hidden overflow-y-auto"
              >
                <div className="p-6 space-y-6">
                  {/* Seção Mobilidade */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">Mobilidade</h3>
                    <div className="space-y-2">
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          navigate('/bike-niteroi');
                          setIsMenuOpen(false);
                        }}
                      >
                        <Bike className="w-4 h-4 mr-3" />
                        Bike Niterói
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          navigate('/transito');
                          setIsMenuOpen(false);
                        }}
                      >
                        <Car className="w-4 h-4 mr-3" />
                        Trânsito
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          navigate('/barcas');
                          setIsMenuOpen(false);
                        }}
                      >
                        <Ship className="w-4 h-4 mr-3" />
                        Barcas
                      </Button>
                    </div>
                  </div>

                  {/* Seção Ferramentas */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">Ferramentas</h3>
                    <div className="space-y-2">
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          window.open("https://chat.niteroi.city", "_blank");
                          setIsMenuOpen(false);
                        }}
                      >
                        <MessageSquare className="w-4 h-4 mr-3" />
                        Niki City AI
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          window.open("https://live-radio-clubnation.pantheonsite.io/", "_blank");
                          setIsMenuOpen(false);
                        }}
                      >
                        <Radio className="w-4 h-4 mr-3" />
                        Rádio
                      </Button>
                    </div>
                  </div>

                  {/* Seção Configurações */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">Configurações</h3>
                    <div className="space-y-2">
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                      >
                        <Bell className="w-4 h-4 mr-3" />
                        Notificações
                      </Button>
                    </div>
                  </div>

                  {/* Seção Data/Hora */}
                  <div className="space-y-3 pt-4 border-t">
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-2 text-lg font-medium">
                        <Clock className="w-5 h-5" />
                        <span>{formatTime(new Date())}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{formatDate(new Date())}</p>
                    </div>
                  </div>

                  {/* Seção Autenticação */}
                  <div className="space-y-3 pt-4 border-t">
                    {/* Remover esta linha:
                    {user ? (
                      <Button
                        variant="destructive"
                        className="w-full"
                        onClick={signOut}
                      >
                        Sair
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            navigate('/login');
                            setIsMenuOpen(false);
                          }}
                        >
                          Entrar
                        </Button>
                        <Button
                          className="w-full"
                          onClick={() => {
                            navigate('/cadastro');
                            setIsMenuOpen(false);
                          }}
                        >
                          Cadastrar
                        </Button>
                      </div>
                    )} */}
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}; 